'use client';

import React from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { IconButton } from '@ui/button';
import { IMAGE_SIZES, ImageCardGroup, ResponsiveImage } from '@web/molecules';
import { ApiResult } from '../../api';
import { useReporter } from '../../reporter';
import { useSessionCustomer } from '../../customer';
import { useShopifyCartContext } from '../OnboardingFlowContainer/ShopifyCartProvider';
import { Question } from '../Question';
import { FormFieldsContainer, FormWrapper } from '../FormFieldsContainer';
import { StyledFormWithImage } from '../StyledForm';
import { onboardingSubmitted, onboardingBackArrowClicked } from '../events';
import {
  FieldWrapper,
  ImageContainer,
  ButtonContainer,
  MobileImageContainer,
  DesktopImageContainer,
} from './styles.js';

export const ActivityForm = ({
  activityQuestionPrompt,
  activityQuestionCallout,
  defaultValues,
  goBack,
  onSubmit,
  index,
  levels,
  subheadline,
  mobileImage,
  desktopImage,
  flowType,
}) => {
  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isSubmitted, isSubmitSuccessful },
    reset,
    getValues,
  } = useForm({
    mode: 'onChange',
    defaultValues,
  });

  const isInSubmittingState =
    isSubmitting || (isSubmitted && isSubmitSuccessful);
  const reporter = useReporter();
  const { customer } = useSessionCustomer();
  const { cart } = useShopifyCartContext();

  const goBackWithReporter = () => {
    reporter.tag(
      onboardingBackArrowClicked('activity_step', cart?.customer || customer),
    );
    goBack();
  };

  const submitForm = async data => {
    reporter.tag(
      onboardingSubmitted(
        index,
        'dog_activity',
        cart?.customer || customer,
        data,
        flowType,
      ),
    );
    const result = await onSubmit(data);
    ApiResult.ifError(result, () =>
      setTimeout(() =>
        reset(getValues(), {
          keepIsSubmitted: false,
        }),
      ),
    );
  };

  return (
    <StyledFormWithImage>
      <ImageContainer>
        <ButtonContainer aria-live="polite">
          <IconButton
            aria-label="go to previous step"
            icon={{
              iconName: 'arrow-left',
              size: '24px',
              alt: 'back',
              id: 'prev_step_footer',
              color: 'background.lightest',
            }}
            onClick={goBackWithReporter}
            disabled={isInSubmittingState}
            type="button"
            variant="primary"
          />
        </ButtonContainer>
        <MobileImageContainer>
          <ResponsiveImage
            priority={true}
            {...mobileImage}
            width={400}
            sizes={IMAGE_SIZES.halfAtLaptop}
          />
        </MobileImageContainer>
        <DesktopImageContainer>
          <ResponsiveImage
            priority={true}
            {...desktopImage}
            height={362}
            width={406}
            sizes={IMAGE_SIZES.halfAtLaptop}
          />
        </DesktopImageContainer>
      </ImageContainer>
      <FormWrapper
        $minHeight={'280'}
        $overflow={activityQuestionCallout ? true : false}
      >
        <FormFieldsContainer>
          <Question
            gap={0}
            prompt={activityQuestionPrompt}
            callout={activityQuestionCallout}
            subheadline={subheadline}
            padding={[0, 0, 0, 0]}
          >
            <FieldWrapper>
              <Controller
                control={control}
                name="activity"
                render={({ field: { ref, ...field } }) => (
                  <>
                    <ImageCardGroup
                      {...field}
                      choices={levels}
                      name="activity"
                      legendText="Select your activity level"
                      cardBorderColor="border.dark"
                      disabled={isInSubmittingState}
                      isLoading={isInSubmittingState}
                      onChange={value => {
                        if (!isInSubmittingState) {
                          field.onChange(value);
                          handleSubmit(submitForm)();
                        }
                      }}
                    />
                  </>
                )}
              />
            </FieldWrapper>
          </Question>
        </FormFieldsContainer>
      </FormWrapper>
    </StyledFormWithImage>
  );
};

ActivityForm.displayName = 'ActivityForm';

ActivityForm.propTypes = {
  activityQuestionPrompt: PropTypes.string.isRequired,
  activityQuestionCallout: PropTypes.object,
  defaultValues: PropTypes.shape({ activity: PropTypes.string }).isRequired,
  goBack: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  levels: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      image: PropTypes.object.isRequired,
    }),
  ).isRequired,
  onSubmit: PropTypes.func.isRequired,
  subheadline: PropTypes.string,
  ctaButtonText: PropTypes.object,
  mobileImage: PropTypes.object.isRequired,
  desktopImage: PropTypes.object.isRequired,
  flowType: PropTypes.string,
};
